import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
} from 'react';
import moment from 'moment';
import intl from 'react-intl-universal';
import { createPortal } from 'react-dom';
import { inject, observer } from 'mobx-react';

import NoX from '../Common/NoX';
import {
  DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH,
} from './SearchRow';
import SearchRow from './SearchRow';
import { alpha } from '../../utils/color';
import ReturnIcon from '../Common/ReturnIcon';
import RootStore from '../../store/RootStore';
import galleryApi from '../../actions/gallery';
import { navigatorTo } from '../../utils/util';
import SimpleBigPic from '../BigPic/SimpleBigPic';
import useSimpleBigPic from '../BigPic/useSimpleBigPic';
import AllForColumn2 from '../GalleryList/AllForColumn2';
import { ThemeContext } from '../../store/ThemeProvider';
import JigsawBottom from '../ComponentNavigator/JigsawBottom';
import { CloseAnimation } from '../GalleryList/GalleryListWrapper';
import { maxNumberByType } from '../ComponentNavigator/jigsaw-limits';

import './SearchResult.scss';

interface ISearchResult {
  close: () => void;
  keyword?: string;
  mode?: 'text' | 'image';
  imagePreview?: string;
  list: any[];
  loading?: boolean;
  onReselectImage?: (base64: string, file?: File) => void;
  onSearchKeyword?: (keyword: string) => void;
  placeholder?: string;
  imageSearchText?: string;
  enableImageSearch?: boolean;
  imageResizeWidth?: number;
  rootStore?: RootStore;
}

const containerEl = document.createElement('div');
const SearchResult = function (props: ISearchResult) {
  const [galleryMap, setGalleryMap] = useState<Record<string, any>>({});
  const [current, setCurrent] = useState(-1);
  const { keyword, mode = 'text', imagePreview, loading = false } = props;
  const [searchInput, setSearchInput] = useState(props.keyword || '');

  useEffect(() => {
    document.body.appendChild(containerEl);
    return () => {
      document.body.removeChild(containerEl);
    };
  }, []);

  useEffect(() => {
    //这个弹窗显示的时候，隐藏掉Video
    window.dispatchEvent(new Event('hide-video'));

    const activeElement = document.activeElement as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLElement
      | null;
    if (activeElement && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }
  }, []);

  useEffect(() => {
    if (mode === 'text') {
      setSearchInput(keyword || '');
    }
  }, [keyword, mode]);

  useEffect(() => {
    return () => {
      props.rootStore?.galleryStore.setJigsaw({
        bottomShow: false,
        jigsawList: [],
        type: '',
      });
    };
  }, [props.rootStore]);

  const { componentColor, h1Color } = useContext(ThemeContext);
  const { galleryStore } = props.rootStore!;

  useEffect(() => {
    const galleryIds = Array.from(
      new Set(
        (props.list || [])
          .map((item: any) => String(item?.galleryid || ''))
          .filter(Boolean)
      )
    );

    if (!galleryIds.length) {
      setGalleryMap({});
      return;
    }

    let cancelled = false;

    galleryApi.batch(galleryIds).then((res: any) => {
      if (cancelled) {
        return;
      }

      const data = Array.isArray(res?.data) ? res.data : [];
      const nextMap = data.reduce((result: Record<string, any>, item: any) => {
        if (item && item.id) {
          result[String(item.id)] = item;
        }
        return result;
      }, {});

      if (window.gallery?.id) {
        nextMap[String(window.gallery.id)] = {
          ...window.gallery,
          id: window.gallery.id,
          link_id: window.pageDetail?.link_id,
          url_labeling: window.gallery?.url_labeling,
          name: window.gallery?.name,
        };
      }

      setGalleryMap(nextMap);
    }).catch((error: any) => {
      console.warn('[SearchResult] gallery batch failed', error);
    });

    return () => {
      cancelled = true;
    };
  }, [props.list]);

  const displayList = useMemo(() => {
    return (props.list || []).map((item: any) => {
      const galleryInfo = galleryMap[String(item.galleryid)] || {};
      const linkId = galleryInfo.link_id || galleryInfo.link || item.link_id;
      const urlLabeling = galleryInfo.url_labeling || item.url_labeling;
      return {
        ...item,
        galleryname:
          item.galleryname || galleryInfo.name || galleryInfo.galleryname,
        link:
          linkId || urlLabeling
            ? {
                type: 'gallery',
                value: String(item.galleryid || galleryInfo.id || ''),
                link_id: linkId,
                url_labeling: urlLabeling,
              }
            : item.link,
      };
    });
  }, [galleryMap, props.list]);

  const { list, columnsClick, setBigIndex, bigIndex, loadBigPic, loadFinish } =
    useSimpleBigPic(displayList);

  useEffect(() => {
    if (bigIndex === -1) {
      const timer = setTimeout(() => {
        setCurrent(bigIndex);
      }, 10);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      setCurrent(bigIndex);
    }, 300);
    return () => clearTimeout(timer);
  }, [bigIndex]);

  const currentGallery = bigIndex > -1 ? list[bigIndex] : null;

  const onImageClick = ({
    index,
    id,
    galleryid,
  }: {
    index?: number;
    id: number;
    galleryid?: string | number;
  }) => {
    const realIndex = list.findIndex(
      (one: any) => Number(one.id) === Number(id)
    );
    if (galleryStore.jigsaw.bottomShow) {
      const nextList = [...galleryStore.jigsaw.jigsawList];
      const currentIndex = nextList.findIndex(
        (one: any) => Number(one.id) === Number(id)
      );

      if (currentIndex > -1) {
        nextList.splice(currentIndex, 1);
      } else {
        if (nextList.length >= maxNumberByType(galleryStore.jigsaw.type)) {
          props.rootStore?.uiStore.showAlert({
            content: intl.get('MAX_DOWNLOAD_NUM', {
              num: maxNumberByType(galleryStore.jigsaw.type),
            }),
          });
          return;
        }

        const currentItem = list[realIndex];
        if (currentItem) {
          nextList.push(currentItem);
        }
      }

      galleryStore.setJigsaw({
        jigsawList: nextList,
      });
      return;
    }

    columnsClick({ index, id });
  };

  const handleClose = () => {
    galleryStore.setJigsaw({
      bottomShow: false,
      jigsawList: [],
      type: '',
    });
    props.close();
  };

  const startDownload = () => {
    galleryStore.setJigsaw({
      bottomShow: true,
      jigsawList: [],
      type: 'download',
    });
  };

  const triggerKeywordSearch = () => {
    const nextKeyword = (searchInput || '').trim();
    if (!nextKeyword || !props.onSearchKeyword) {
      return;
    }
    props.onSearchKeyword(nextKeyword);
  };

  const zipFileName = `${(
    keyword || (mode === 'image' ? '以图搜图' : '搜索结果')
  ).replace(/[\\/:*?"<>|\s]+/g, '_')}_${moment().format(
    'YYYY_MM_DD_HH_mm_ss'
  )}.zip`;

  const countText = mode === 'image' ? `找到 ${list.length} 张相似图片` : `共识别出 ${list.length} 张图片`;

  const resultText = loading
    ? '正在搜索中...'
    : list.length > 0
    ? countText
    : '未找到相关图片';

  return createPortal(
    <div
      className="search-result"
      style={{ backgroundColor: alpha(componentColor, 1) }}
    >
      <div className="title" style={{ color: h1Color}}>
        {/* {mode === 'image' ? '图片搜索结果' : '文字搜索结果'} */}
      </div>
      <div className="search-result-top-search">
        <SearchRow
          value={searchInput}
          onInputChange={setSearchInput}
          placeholder={
            props.placeholder ||
            intl.get('Enter your participate no., letters included')
          }
          search={triggerKeywordSearch}
          searchByImage={props.onReselectImage}
          enableImageSearch={props.enableImageSearch}
          imageSearchText={props.imageSearchText}
          imageResizeWidth={
            props.imageResizeWidth || DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH
          }
        />
      </div>
      <div className="search-result-header">
        {mode === 'image' && imagePreview ? (
          <div className="search-result-summary">
            <img src={imagePreview} alt="search preview" />
            <div className="search-result-summary-content">
              <div className="search-result-summary-text">{resultText}</div>
              {!loading ? (
                <div className="search-result-link-btn" style={{display: 'none'}}>
                  点击上方“以图搜图”可重新上传
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            {keyword ? (
              <div className="search-result-keyword">{keyword}</div>
            ) : null}
            {!loading ? (
              <div className="search-result-summary-text only-text">
                {resultText}
              </div>
            ) : null}
          </>
        )}
      </div>
      {loading ? (
        <div className="search-result-loading">正在搜索中...</div>
      ) : list.length === 0 ? (
        <NoX text={resultText} />
      ) : (
        <div className="gallery-list">
          <AllForColumn2
            list={list}
            onClick={onImageClick}
            jigsaw={galleryStore.jigsaw}
            lazy={false}
            current={bigIndex}
          />
          <CloseAnimation
            containerSelectorFunction={() => '.search-result'}
            targetElementSelectorFunction={(item: any) =>
              `.search-result .img-wrapper[data-id="${item.id}"]`
            }
            list={list}
            index={bigIndex}
          />
        </div>
      )}
      {!loading && galleryStore.jigsaw.bottomShow ? (
        <JigsawBottom
          type="download"
          list={galleryStore.jigsaw.jigsawList}
          zipFileName={zipFileName}
          onCancel={() => {
            galleryStore.setJigsaw({
              bottomShow: false,
              jigsawList: [],
              type: '',
            });
          }}
          onConfirm={() => {}}
        />
      ) : null}
      <div className="search-result-return-fixed">
        <ReturnIcon onClick={handleClose} />
      </div>

      {!loading && list.length > 0 && !galleryStore.jigsaw.bottomShow ? (
        <div className="search-result-fixed-actions">
          <button
            type="button"
            className="search-result-download-btn"
            onClick={startDownload}
          >
            下载图片
          </button>
        </div>
      ) : null}
      <SimpleBigPic
        pageType="SearchResult"
        index={current}
        list={list}
        setIndex={setBigIndex}
        loadBigPic={loadBigPic}
        loadFinish={loadFinish}
        bigPicTopInfoRender={
          currentGallery && currentGallery.link ? (
            <span
              className="bigpic-top-info"
              onClick={() => {
                if (currentGallery.link) {
                  navigatorTo(currentGallery.link);
                }
              }}
            >
              <span className="bigpic-top-info-label">
                <span className="bigpic-top-info-prefix">相册</span>
                {currentGallery.galleryname ? (
                  <span className="bigpic-top-info-name">
                    ：{currentGallery.galleryname}
                  </span>
                ) : null}
              </span>
              <span className="bigpic-top-info-arrow" aria-hidden="true" />
            </span>
          ) : null
        }
      />
    </div>,
    containerEl
  );
};

export default inject('rootStore')(observer(SearchResult));
